import { DateTimeRange } from '@modules/common/components/DateRangePicker/types';
import { NAV_PATHS, Widgets } from '@modules/common/constants';
import { isEmptyWhereClause } from '@modules/common/utils';
import { VisualisationType } from '@modules/quantify/store/types';
import { getQuantifyAnalysisURL } from '@modules/quantify/utils';
import { isEqual } from 'lodash';
import { compressToEncodedURIComponent } from 'lz-string';
import { FieldNames, FilterKeyNames } from 'v2/core/entities/fields';
import { LabelType } from 'v2/core/entities/taxonomy';
import { combineWhereClauses } from 'v2/core/services/queryBuilder/combineWhereClauses';
import {
  EmptyWhereClause,
  OperationType,
  WhereClause,
} from 'v2/core/services/queryBuilder/whereClause';
import { toWhereClauseFromBaseCondition } from '.';
import { BaseCondition } from '../types';

interface getUseInQuantifyV2LinkOptions {
  widget: Widgets;
  baseCondition?: BaseCondition | null;
  whereClause?: WhereClause;
  duration?: DateTimeRange;
  shouldShowCooccurringDataPoints?: boolean;
}

export function getUseInQuantifyV2Link({
  widget,
  baseCondition,
  whereClause,
  duration,
  shouldShowCooccurringDataPoints,
}: getUseInQuantifyV2LinkOptions) {
  // ##TODO find the right API
  if (
    baseCondition?.type === LabelType.Aspect ||
    baseCondition?.type === LabelType.Reason
  ) {
    const whereClauseStr = JSON.stringify(whereClause);
    if (
      whereClauseStr.includes(FilterKeyNames.EntityFilter) ||
      whereClauseStr.includes(FilterKeyNames.ReasonFilter)
    ) {
      return null;
    }
  }

  if (
    widget === Widgets.FeedbackVolume &&
    baseCondition &&
    baseCondition.type === null
  ) {
    return null;
  }

  const isReasonWidget = widget === Widgets.Reason;

  const getGroupByValue = () => {
    if (isReasonWidget) {
      return FieldNames.Reason;
    }
    if (widget === Widgets.Source) {
      return FieldNames.Source;
    }
    if (widget === Widgets.ReviewSentiment) {
      return FieldNames.ReviewSentiment;
    }
    if (widget === Widgets.FeedbackVolume) {
      return baseCondition?.type === LabelType.Reason
        ? FieldNames.Reason
        : FieldNames.Entity;
    }
    return FieldNames.Entity;
  };

  const getActiveVisualisationType = () => {
    switch (widget) {
      case Widgets.Reason:
        return VisualisationType.BAR;
      case Widgets.Source:
      case Widgets.ReviewSentiment:
        return VisualisationType.PIE;
      case Widgets.TrackedKeywordCategory:
        return VisualisationType.BAR;
      default:
        return undefined;
    }
  };

  const getShouldShowCategories = () => {
    return widget === Widgets.TrackedKeywordCategory;
  };

  const getWhereClause = () => {
    const where =
      whereClause && isEmptyWhereClause(whereClause) ? undefined : whereClause;

    let baseConditionWhereClause: WhereClause | undefined =
      toWhereClauseFromBaseCondition(baseCondition);

    baseConditionWhereClause = isEqual(
      baseConditionWhereClause,
      EmptyWhereClause,
    )
      ? undefined
      : baseConditionWhereClause;

    if (where && baseConditionWhereClause) {
      return combineWhereClauses(
        baseConditionWhereClause,
        where,
        OperationType.AND,
      );
    }

    return where || baseConditionWhereClause;
  };

  const search = compressToEncodedURIComponent(
    getQuantifyAnalysisURL({
      groupByValue: getGroupByValue(),
      shouldShowCategories: getShouldShowCategories(),
      activeVisualisationType: getActiveVisualisationType(),
      duration: duration as DateTimeRange,
      whereClauseConfig: {
        whereClause: getWhereClause() ?? null,
      },
      shouldShowCooccurringDataPoints: shouldShowCooccurringDataPoints,
    }),
  );

  return {
    pathname: `/${NAV_PATHS.QUANTIFY}`,
    search,
  };
}
